import { createFileRoute, notFound } from "@tanstack/react-router";
import { InfoLayout } from "@/components/laneguide/InfoLayout";
import { ArticleBody } from "@/components/laneguide/ArticleBody";
import { SERVICE_PAGES as POSTS } from "@/lib/service";
const SITE = "https://svensklaneguide.se";

export const Route = createFileRoute("/$hubSlug")({
  head: ({ params }) => {
    const p = POSTS[params.hubSlug];
    const title = p ? `${p.title} | Svensk Låneguide` : "Guide | Svensk Låneguide";
    const desc = p?.intro.slice(0, 155) ?? "Jämförelser och guider om lån, räntor och långivare.";
    const url = `${SITE}/${params.hubSlug}`;
    const img = p?.heroSrc ? `${SITE}${p.heroSrc}` : undefined;

    const meta: Record<string, string>[] = [
      { title },
      { name: "description", content: desc },
      { name: "robots", content: p ? "index, follow" : "noindex" },
      { property: "og:title", content: title },
      { property: "og:description", content: desc },
      { property: "og:type", content: "article" },
      { property: "og:url", content: url },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: title },
      { name: "twitter:description", content: desc },
    ];
    if (img) {
      meta.push({ property: "og:image", content: img });
      meta.push({ name: "twitter:image", content: img });
    }

    const scripts: { type: string; children: string }[] = [];
    if (p) {
      scripts.push({
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          headline: p.title,
          description: desc,
          inLanguage: "sv-SE",
          mainEntityOfPage: url,
          ...(img ? { image: img } : {}),
          ...(p.published ? { datePublished: p.published } : {}),
          ...(p.updated ? { dateModified: p.updated } : {}),
          author: { "@type": "Person", name: p.author ?? "Svensk Låneguides redaktion" },
          publisher: { "@type": "Organization", name: "Svensk Låneguide", url: SITE },
        }),
      });
      scripts.push({
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Hem", item: SITE },
            { "@type": "ListItem", position: 2, name: "Guider", item: `${SITE}/nyhet` },
            { "@type": "ListItem", position: 3, name: p.title, item: url },
          ],
        }),
      });
      if (p.faq?.length) {
        scripts.push({
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: p.faq.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }),
        });
      }
    }

    const links: Record<string, string>[] = [{ rel: "canonical", href: url }];
    if (p?.heroSrc) {
      links.push({ rel: "preload", as: "image", href: p.heroSrc, fetchPriority: "high" });
    }

    return { meta, links, scripts };
  },
  loader: ({ params }) => {
    if (!POSTS[params.hubSlug]) throw notFound();
    return { slug: params.hubSlug };
  },
  component: ServicePage,
});

function ServicePage() {
  const { hubSlug } = Route.useParams();
  const post = POSTS[hubSlug]!;
  return (
    <InfoLayout title={post.title} faq={post.faq}>
      <ArticleBody
        post={post}
        section={{ label: "Guider", to: "/nyhet" }}
        calculatorPosition="afterReviews"
        epi={hubSlug}
      />
    </InfoLayout>
  );
}
